import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { DataService } from 'src/app/data.service';

/**
 *  UTÓLAGOS HELYETTESÍTÉS – bejelentés és jóváhagyás egy képernyőn.
 *
 *  Nem mindig van idő előre kiírni a helyettesítést: valaki reggel megbetegszik, a
 *  kolléga bemegy az órára. Az órarendben viszont az EREDETI tanár marad, tehát a
 *  megtartott óra papíron meg sem történt – nem kerül a helyettesítési listára, és nem
 *  jut el a bérszámfejtésig.
 *
 *  A képernyő szerepkör szerint bővül: a kolléga bejelent, az adminisztrátor elbírál.
 *  Ez azért egy oldal, mert ugyanarról a dologról szól – aki mindkettő, annak ne
 *  kelljen két helyen keresgélnie, hogy mi lett a bejelentésével.
 *
 *  Az ÓRAREND a jóváhagyásig érintetlen marad: a bejelentés állítás, a helyettesítés
 *  tény. A szerver ugyanezt kényszeríti ki, a felület nem tud rajta lazítani.
 */
@Component({
  selector: 'app-helyettesites-bejelentes',
  templateUrl: './helyettesites-bejelentes.component.html',
  styleUrls: ['./helyettesites-bejelentes.component.scss'],
})
export class HelyettesitesBejelentesComponent implements OnInit {
  intezmeny = '';
  allomany = '';
  admin = false;
  bekapcsolva = false;
  automata = false;
  elmaradtTol = '';
  elmaradtIg = '';
  elmaradtFut = false;
  toltodik = false;
  hiba = '';

  /* bejelentés */
  datum = '';
  osztaly = '';
  osztalyok: any[] = [];
  /*  Hányadik óra – nem kötelező. Egy napon húsz-harminc óra is lehet; ha a kolléga
   *  tudja, melyik órát tartotta, ne kelljen az egészet átnéznie. */
  oraSzam: number | null = null;
  orak: any[] = null;
  /** Tanárra szűrés a betöltött órák között (az órarend szerinti tanár). */
  tanarSzuro = '';
  get tanarok(): string[] {
    return [...new Set((this.orak || []).map((o: any) => String(o.tanar_nev || '')).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'hu'));
  }
  get szurtOrak(): any[] {
    const sz = this.tanarSzuro && this.tanarok.includes(this.tanarSzuro) ? this.tanarSzuro : '';
    return (this.orak || []).filter((o: any) => !sz || String(o.tanar_nev || '') === sz);
  }
  orakFut = false;
  valasztottOra: any = null;
  tipus = 'E';
  mod = 'H';
  indok = '';
  kuldFut = false;

  /* lista */
  nezet: 'sajat' | 'fuggo' | 'mind' = 'sajat';
  tetelek: any[] = [];
  dontesIndok: { [id: string]: string } = {};
  dontesFut = '';

  readonly TIPUSOK = [{ e: 'E', n: 'Eseti' }, { e: 'T', n: 'Tartós' }];
  readonly MODOK = [
    { e: 'H', n: 'Helyettesítés' }, { e: 'O', n: 'Összevonás' },
    { e: 'E', n: 'Elmarad' }, { e: 'S', n: 'Szívességi' },
  ];
  readonly ALLAPOT_NEV: any = {
    fuggo: 'jóváhagyásra vár', jovahagyva: 'jóváhagyva',
    elutasitva: 'elutasítva', visszavonva: 'visszavonva',
  };

  constructor(private http: HttpClient, private data: DataService) {}

  async ngOnInit() {
    const sajat = await this.data.getFromStorage('sajatIntezmeny');
    this.intezmeny = sajat?.intezmenyId || '';
    const um = await this.data.getFromStorage('userMetadata');
    this.allomany = String(um?.adid || um?.username || um?.preferred_username || '').trim();
    this.datum = new Date().toISOString().slice(0, 10);
    await this.betolt();
    this.osztalyokBetolt();
  }

  private get alap(): string {
    return `/api/helyettesites-bejelentes`;
  }

  async osztalyokBetolt() {
    try {
      const ev = await this.data.getMunkatanev();
      this.osztalyok = await firstValueFrom(
        this.http.get<any[]>(`/api/osztaly/list/${ev}/${this.intezmeny}`)) || [];
    } catch (e) { this.osztalyok = []; }
  }

  /** A lista + a szerepkör + a kapcsoló állapota egyetlen hívásból. */
  async betolt() {
    if (!this.intezmeny) return;
    this.toltodik = true;
    this.hiba = '';
    try {
      const p = new URLSearchParams({ allomany: this.allomany });
      if (this.nezet === 'fuggo') p.set('allapot', 'fuggo');
      if (this.nezet === 'sajat') p.set('csak_sajat', '1');
      const r: any = await firstValueFrom(this.http.get(
        `${this.alap}/lista/${this.intezmeny}?${p.toString()}`));
      this.admin = !!r?.admin;
      this.bekapcsolva = !!r?.bekapcsolva;
      this.tetelek = r?.tetelek || [];
      if (this.admin) {
        try {
          const b: any = await firstValueFrom(this.http.get(`${this.alap}/beallitas/${this.intezmeny}`));
          this.automata = !!b?.automata;
        } catch { /* a kapcsoló állapota nélkül is használható a lista */ }
      }
    } catch (e: any) {
      this.hiba = e?.error?.error || 'A bejelentések lekérése nem sikerült.';
      this.tetelek = [];
    } finally {
      this.toltodik = false;
    }
  }

  /** A funkció be/ki kapcsolása (csak adminisztrátornak). */
  async kapcsoloValt(be: boolean) {
    try {
      await firstValueFrom(this.http.put(`${this.alap}/beallitas/${this.intezmeny}`,
        { bekapcsolva: be, allomany: this.allomany }));
      this.bekapcsolva = be;
      this.data.sendToast(be
        ? 'Bekapcsolva: a kollégák mostantól bejelenthetik az utólagos helyettesítést.'
        : 'Kikapcsolva.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A beállítás mentése nem sikerült.', 'error');
    }
  }

  /** Automatikus jóváhagyás be/ki (csak adminisztrátornak). */
  async automataValt(be: boolean) {
    try {
      await firstValueFrom(this.http.put(`${this.alap}/beallitas/${this.intezmeny}`,
        { automata: be, allomany: this.allomany }));
      this.automata = be;
      this.data.sendToast(be
        ? 'Automatikus jóváhagyás bekapcsolva: az egyértelmű bejelentések azonnal az órarendbe kerülnek.'
        : 'Automatikus jóváhagyás kikapcsolva: a bejelentések újra jóváhagyásra várnak.', 'success');
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A beállítás mentése nem sikerült.', 'error');
    }
  }

  /* ── bejelentés ─────────────────────────────────────────────────────── */

  async orakBetolt() {
    if (!this.datum) { this.data.sendToast('Adj meg dátumot.', 'warning'); return; }
    this.orakFut = true;
    this.orak = null;
    this.valasztottOra = null;
    try {
      const p = new URLSearchParams({ datum: this.datum, allomany: this.allomany });
      if (this.oraSzam) p.set('ora', String(this.oraSzam));
      if (this.osztaly) p.set('osztaly', this.osztaly);
      this.orak = await firstValueFrom(this.http.get<any[]>(
        `${this.alap}/orak/${this.intezmeny}?${p.toString()}`));
    } catch (e: any) {
      this.orak = [];
      this.data.sendToast(e?.error?.error || 'Az órák lekérése nem sikerült.', 'error');
    } finally {
      this.orakFut = false;
    }
  }

  oraCim(o: any): string {
    const hol = o.csoport_nev || o.osztaly_nev || '';
    //  A terem is belefér: a bejelentő gyakran arról ismeri fel, melyik óráról van szó.
    return `${o.ora}. óra – ${hol}${o.tantargy_nev ? ' · ' + o.tantargy_nev : ''}`
      + `${o.terem ? ' · ' + o.terem : ''}`;
  }

  async bejelent() {
    if (!this.valasztottOra) { this.data.sendToast('Válaszd ki az órát.', 'warning'); return; }
    this.kuldFut = true;
    try {
      const r: any = await firstValueFrom(this.http.post(`${this.alap}/bejelentes`, {
        intezmeny: this.intezmeny,
        ora: this.valasztottOra.id,
        bejelento: this.allomany,
        tipus: this.tipus,
        mod: this.mod,
        indok: this.indok || null,
      }));
      this.data.sendToast(r?.automatikus
        ? 'Bejelentve és jóváhagyva: a helyettesítés bekerült az órarendbe. Ha tévedtél, alul visszavonhatod.'
        : 'Bejelentve. Az adminisztrátor jóváhagyásáig az órarend nem változik.', 'success');
      this.valasztottOra = null;
      this.indok = '';
      await this.orakBetolt();
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A bejelentés nem sikerült.', 'error');
    } finally {
      this.kuldFut = false;
    }
  }

  /* ── elbírálás ──────────────────────────────────────────────────────── */

  async dontes(b: any, dontes: 'jovahagy' | 'elutasit', felulir = false) {
    const indok = (this.dontesIndok[b.id] || '').trim();
    if (dontes === 'elutasit' && !indok) {
      this.data.sendToast('Az elutasításhoz indokot kell megadni.', 'warning');
      return;
    }
    this.dontesFut = b.id;
    try {
      const r: any = await firstValueFrom(this.http.post(`${this.alap}/dontes/${b.id}`, {
        dontes, dontotte: this.allomany, indok: indok || null, felulir,
      }));
      this.data.sendToast(dontes === 'jovahagy'
        ? `Jóváhagyva – bekerült az órarendbe.`
          + (r?.tovabbi_elutasitva ? ` ${r.tovabbi_elutasitva} másik bejelentés elutasítva ugyanerre az órára.` : '')
        : 'Elutasítva.', 'success');
      this.dontesIndok[b.id] = '';
      await this.betolt();
    } catch (e: any) {
      // 409: az órán már más a helyettesítő – ez bérre ható döntés, ezért rákérdezünk.
      if (e?.status === 409 && e?.error?.jelenlegi_helyettesito) {
        const nev = b.ora_helyettesito_nev || e.error.jelenlegi_helyettesito;
        if (confirm(`Ezen az órán jelenleg ${nev} van helyettesítőként rögzítve. `
          + 'A jóváhagyás felülírja – ez érinti a helyettesítési elszámolást. Folytatod?')) {
          this.dontesFut = '';
          return this.dontes(b, dontes, true);
        }
      } else {
        this.data.sendToast(e?.error?.error || 'A döntés nem sikerült.', 'error');
      }
    } finally {
      this.dontesFut = '';
    }
  }

  async visszavon(b: any) {
    const indok = (this.dontesIndok[b.id] || '').trim();
    if (!indok) { this.data.sendToast('A visszavonáshoz indokot kell megadni.', 'warning'); return; }
    if (!confirm('Visszavonod a jóváhagyást? A helyettesítés kikerül az órarendből.')) return;
    this.dontesFut = b.id;
    try {
      const r: any = await firstValueFrom(this.http.post(`${this.alap}/visszavon/${b.id}`,
        { dontotte: this.allomany, indok }));
      this.data.sendToast(r?.orarend_visszaallitva
        ? 'Visszavonva, az órarendből is kikerült.'
        : 'Visszavonva. Az órarendben időközben más helyettesítő szerepel – azt nem bántottuk.', 'success');
      this.dontesIndok[b.id] = '';
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
    } finally {
      this.dontesFut = '';
    }
  }

  /** Saját órák elmaradtnak jelölése egy időszakra (az adathiba-modulon át: naplózva, visszavonható). */
  async elmaradtJelol() {
    const adatok = { tanar: this.allomany, tol: this.elmaradtTol, ig: this.elmaradtIg || this.elmaradtTol, ok: 'Helyettesítés bejelentése oldalról' };
    this.elmaradtFut = true;
    try {
      const elo: any = await firstValueFrom(this.http.post('/api/adatjavitas/bejelentes',
        { intezmeny: this.intezmeny, tipus: 'ora_elmaradt', adatok, proba: true }));
      const fig = (elo?.eredmeny?.figyelmeztetes || []).join('\n');
      if (!elo?.valtozas_db) { this.data.sendToast(fig || 'Nincs elmaradtnak jelölhető órád ebben az időszakban.', 'warning'); return; }
      if (!confirm(`${elo.valtozas_db} órád kerül „elmaradt” jelölésre.${fig ? '\n\n' + fig : ''}\n\nRendben?`)) return;
      await firstValueFrom(this.http.post('/api/adatjavitas/bejelentes', { intezmeny: this.intezmeny, tipus: 'ora_elmaradt', adatok }));
      this.data.sendToast(`${elo.valtozas_db} óra elmaradtként jelölve.`, 'success');
      this.elmaradtTol = ''; this.elmaradtIg = '';
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'Nem sikerült jelölni.', 'error');
    } finally { this.elmaradtFut = false; }
  }

  /** A saját, még el nem bírált bejelentés visszavonása (pl. kiderült, hogy mégse kell). */
  sajatE(b: any): boolean {
    return !!this.allomany && String(b.bejelento || '').toLowerCase() === this.allomany.toLowerCase();
  }

  async sajatVisszavon(b: any) {
    if (!confirm(b.allapot === 'jovahagyva'
      ? 'Visszavonod a bejelentésedet? A helyettesítés kikerül az órarendből.'
      : 'Visszavonod a bejelentésedet? (Ha mégis kell, újra beküldheted.)')) return;
    this.dontesFut = b.id;
    try {
      await firstValueFrom(this.http.post(`${this.alap}/sajat-visszavon/${b.id}`, { allomany: this.allomany }));
      this.data.sendToast('A bejelentésed visszavonva.', 'success');
      await this.betolt();
    } catch (e: any) {
      this.data.sendToast(e?.error?.error || 'A visszavonás nem sikerült.', 'error');
    } finally {
      this.dontesFut = '';
    }
  }

  tipusNev(e: string) { return this.TIPUSOK.find((x) => x.e === e)?.n || e; }
  modNev(e: string) { return this.MODOK.find((x) => x.e === e)?.n || e; }
}
